import { Link } from "react-router-dom";
import { UserRound, ArrowUpRight, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { OWNER, TEAM_MEMBERS, TEAM_PLACEHOLDERS } from "@/data";

export default function MeetTeamPage() {
  return (
    <>
      <PageHeader
        overline="Our People"
        title="Meet the"
        accent="team."
        subtitle="A growing family of licensed professionals dedicated to protecting yours."
      />

      <section className="relative py-12 md:py-16" style={{ background: "linear-gradient(180deg, #071233 0%, #0a1a44 45%, #050b1d 100%)" }} data-testid="team-section-royal">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_legacy-secure-12/artifacts/2qpmd42r_3DB25B7B-F640-4F98-8D9A-03052B5EE5F4.png"
              alt="Heritage Family Protection Agency — Meet the Team"
              data-testid="team-cover-image"
              className="w-full h-auto rounded-3xl border border-[#d4af37]/35 shadow-[0_25px_70px_rgba(0,0,0,0.55)] mb-12"
            />
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Reveal>
              <div data-testid="team-card-owner" className="group relative h-full overflow-hidden rounded-3xl border border-[#bf953f]/35 bg-[#0a192f]">
                <div className="relative overflow-hidden">
                  <img src={OWNER.photo} alt={OWNER.name} className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f] via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl text-white">{OWNER.name}</h3>
                  <p className="mt-1 font-body text-sm text-gold-gradient inline-block">{OWNER.title}</p>
                  <a
                    href={`mailto:${OWNER.email}`}
                    data-testid="team-owner-email"
                    className="mt-3 flex items-center gap-1.5 font-body text-xs text-white/55 hover:text-[#d4af37] transition-colors break-all"
                  >
                    <Mail size={13} className="shrink-0 text-[#d4af37]" /> {OWNER.email}
                  </a>
                  <Link
                    to="/meet-the-owner"
                    data-testid="team-owner-link"
                    className="mt-4 flex items-center gap-1.5 font-body text-xs tracking-widest uppercase text-[#d4af37] hover:text-[#fcf6ba] transition-colors"
                  >
                    Full Bio <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>

            {TEAM_MEMBERS.map((m, i) => (
              <Reveal key={m.name} delay={0.06 + i * 0.05}>
                <div data-testid={`team-card-member-${i}`} className="group relative h-full overflow-hidden rounded-3xl border border-[#bf953f]/35 bg-[#0a192f]">
                  <div className="relative overflow-hidden">
                    {m.photo ? (
                      <img src={m.photo} alt={m.name} className="w-full aspect-[4/5] object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
                    ) : (
                      <div className="w-full aspect-[4/5] flex items-center justify-center bg-[#0d0d10]">
                        <span className="inline-flex h-20 w-20 items-center justify-center rounded-full border border-[#bf953f]/30 text-[#d4af37]/60">
                          <UserRound size={34} strokeWidth={1.2} />
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f] via-transparent to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif-display text-2xl text-white">{m.name}</h3>
                    <p className="mt-1 font-body text-sm text-gold-gradient inline-block">{m.shortTitle}</p>
                    {m.email && (
                      <a
                        href={`mailto:${m.email}`}
                        data-testid={`team-member-email-${i}`}
                        className="mt-3 flex items-center gap-1.5 font-body text-xs text-white/55 hover:text-[#d4af37] transition-colors break-all"
                      >
                        <Mail size={13} className="shrink-0 text-[#d4af37]" /> {m.email}
                      </a>
                    )}
                    {m.bio && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          data-testid={`team-member-bio-btn-${i}`}
                          className="mt-4 flex items-center gap-1.5 font-body text-xs tracking-widest uppercase text-[#d4af37] hover:text-[#fcf6ba] transition-colors"
                        >
                          Read Bio <ArrowUpRight size={14} />
                        </button>
                      </DialogTrigger>
                      <DialogContent
                        data-testid={`team-member-bio-dialog-${i}`}
                        className="max-w-2xl max-h-[85vh] overflow-y-auto border-[#bf953f]/30 bg-[#0a192f] p-0"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr]">
                          <img src={m.photo} alt={m.name} className="hidden sm:block h-full w-full object-cover object-top" />
                          <div className="p-7">
                            <DialogTitle className="font-serif-display text-3xl font-medium text-white">{m.name}</DialogTitle>
                            <DialogDescription asChild>
                              <p className="mt-1 font-body text-sm text-gold-gradient">{m.title}</p>
                            </DialogDescription>
                            <div className="mt-5 space-y-4">
                              {m.bio.map((p, j) => (
                                <p key={j} className="font-body font-light text-sm md:text-base leading-relaxed text-white/70">{p}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}

            {TEAM_PLACEHOLDERS.map((t, i) => (
              <Reveal key={i} delay={0.08 + i * 0.06}>
                <div data-testid={`team-card-placeholder-${i}`} className="h-full flex flex-col overflow-hidden rounded-3xl border border-dashed border-[#d4af37]/30 bg-[#0a192f]">
                  <div className="flex-1 min-h-[280px] flex items-center justify-center aspect-[4/5]">
                    <span className="inline-flex h-20 w-20 items-center justify-center rounded-full border border-[#bf953f]/30 text-[#d4af37]/50">
                      <UserRound size={34} strokeWidth={1.2} />
                    </span>
                  </div>
                  <div className="p-6 border-t border-white/10">
                    <h3 className="font-serif-display text-2xl text-white/45">Coming Soon</h3>
                    <p className="mt-1 font-body text-sm text-white/35">{t.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-16 rounded-3xl border border-[#d4af37]/35 bg-gradient-to-br from-[#0c1c40] to-[#071233] p-10 md:p-14 text-center shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                Our team is <span className="text-gold-gradient italic">growing.</span>
              </h2>
              <p className="mt-4 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
                We're building a family of agents and associates who protect families for a living.
                Think you belong on this wall?
              </p>
              <Link
                to="/careers"
                data-testid="team-join-btn"
                className="btn-sweep mt-8 inline-flex items-center rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-medium tracking-wide text-black"
              >
                Join the Team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
